import React, { useState } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import ResizableButton from './ResizableButton';

export default function ResizableButtonDemo() {
  const [buttons, setButtons] = useState([
    {
      id: 'btn1',
      content: 'Click Me!',
      styles: {
        backgroundColor: '#ff6b35',
        color: '#ffffff',
        padding: '12px 24px',
        fontSize: '16px',
        fontWeight: 'bold',
        borderRadius: '4px',
        width: 200,
        height: 48
      }
    },
    {
      id: 'btn2',
      content: 'Resize Me!',
      styles: {
        backgroundColor: '#4caf50',
        color: '#ffffff',
        padding: '16px 32px',
        fontSize: '18px',
        fontWeight: 'bold',
        borderRadius: '8px',
        width: 250,
        height: 56
      }
    },
    {
      id: 'btn3',
      content: 'Drag to Resize',
      styles: {
        backgroundColor: '#2630f3',
        color: '#ffffff',
        padding: '20px 40px',
        fontSize: '20px',
        fontWeight: 'bold',
        borderRadius: '12px',
        width: 300,
        height: 64
      }
    }
  ]);

  const updateButton = (buttonId, updates) => {
    setButtons(prev => prev.map(btn => 
      btn.id === buttonId 
        ? { ...btn, ...updates }
        : btn
    ));
  };

  return (
    <Box sx={{ p: 4, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 3, textAlign: 'center' }}>
        Resizable Button Demo
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', color: 'text.secondary' }}>
        Hover over buttons to see resize handles. Drag the handles to resize buttons in real-time!
      </Typography>

      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Basic Buttons
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
          {buttons.map((button) => (
            <ResizableButton
              key={button.id}
              element={button}
              updateElement={(id, updates) => updateButton(id, updates)}
            >
              <button
                style={{
                  backgroundColor: button.styles.backgroundColor,
                  color: button.styles.color,
                  padding: button.styles.padding,
                  fontSize: button.styles.fontSize,
                  fontWeight: button.styles.fontWeight,
                  borderRadius: button.styles.borderRadius,
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                  }
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                }}
              >
                {button.content}
              </button>
            </ResizableButton>
          ))}
        </Box>
      </Paper>

      <Paper sx={{ p: 4 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Instructions
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>Hover over any button to see the resize handles appear</li>
          <li>Drag corner handles to resize both width and height</li>
          <li>Drag edge handles to resize only width or height</li>
          <li>Buttons maintain their aspect ratio and styling during resize</li>
          <li>Minimum size: 100px × 40px, Maximum size: 800px × 200px</li>
        </Box>
      </Paper>
    </Box>
  );
}
